/**
 * Validation at the system boundary.
 *
 * Every value that arrives from a client passes through one of these schemas
 * before it reaches business logic. Nothing downstream re-validates shapes;
 * this is the one place that decides what a well-formed request looks like.
 */

import { z } from "zod";
import { ID_PREFIXES, isId, type Id, type IdKind } from "./ids.js";
import { CAPABILITIES, GROUP_ROLES } from "./permissions.js";
import { GALLERY_SORTS, MAX_PAGE_SIZE } from "./cursor.js";
import { FILE_KINDS, VARIANTS } from "./media.js";

/** A Zod schema for a prefixed identifier, preserving the branded type. */
export function zId<K extends IdKind>(kind: K) {
  const prefix = ID_PREFIXES[kind];
  return z
    .string()
    .regex(
      new RegExp(`^${prefix}_[0-9a-hjkmnp-tv-z]{26}$`),
      `Expected a ${kind} id`,
    )
    .transform((value) => value as Id<K>);
}

/* ────────────────────────────────── Auth ─────────────────────────────────── */

export const usernameSchema = z
  .string()
  .trim()
  .min(3, "Username must be at least 3 characters.")
  .max(32, "Username must be 32 characters or fewer.")
  .regex(
    /^[a-z0-9._-]+$/i,
    "Use letters, numbers, dots, underscores or hyphens.",
  )
  // Reserved so they cannot collide with routes or be used to impersonate.
  .refine(
    (value) =>
      !["admin", "root", "api", "system", "support", "help", "me"].includes(
        value.toLowerCase(),
      ),
    {
      message: "That username isn't available.",
    },
  );

export const passwordSchema = z
  .string()
  .min(10, "Use at least 10 characters.")
  .max(200, "That password is too long.")
  // Length beats composition rules: a long passphrase is stronger and easier to
  // remember than "P@ssw0rd!". We only reject the genuinely hopeless.
  .refine((value) => new Set(value).size >= 5, {
    message: "That password is too repetitive.",
  });

export const displayNameSchema = z.string().trim().min(1).max(60);

export const totpCodeSchema = z
  .string()
  .trim()
  .regex(/^\d{6}$/, "Enter the 6-digit code from your authenticator app.");

export const accessCodeSchema = z.string().trim().min(4).max(64);

export const loginInput = z.object({
  username: z.string().trim().min(1).max(64),
  password: z.string().min(1).max(200),
  totpCode: totpCodeSchema.optional(),
  deviceName: z.string().trim().max(80).optional(),
});

export const nameSchema = z
  .string()
  .trim()
  .min(1, "This is required.")
  .max(80)
  // Names are not ASCII and not two words. The only thing worth rejecting is a
  // value that is plainly not a name at all; anything stricter turns away real
  // people, and every rule anyone has tried has done exactly that.
  .refine((value) => !/^\s*$/.test(value), "This is required.");

/**
 * The age at which a person may consent for themselves.
 *
 * GDPR sets this between 13 and 16 and leaves the choice to each member state;
 * Poland chose 16. Below it, consent has to come from whoever holds parental
 * responsibility — which this service has no way to obtain, so it declines the
 * registration instead of pretending otherwise.
 *
 * This is the *only* thing the birth date is used for. If that ever stops being
 * true, the field should be removed rather than quietly repurposed.
 */
export const MINIMUM_AGE_YEARS = 16;

export function ageOn(dateOfBirth: string, on: Date): number {
  const born = new Date(`${dateOfBirth}T00:00:00Z`);
  let age = on.getUTCFullYear() - born.getUTCFullYear();
  const monthDelta = on.getUTCMonth() - born.getUTCMonth();
  // Not yet had this year's birthday.
  if (
    monthDelta < 0 ||
    (monthDelta === 0 && on.getUTCDate() < born.getUTCDate())
  )
    age -= 1;
  return age;
}

/** `YYYY-MM-DD`. No time and no zone: a birthday has neither. */
export const dateOfBirthSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Use the date picker.")
  .refine(
    (value) => !Number.isNaN(Date.parse(`${value}T00:00:00Z`)),
    "That date does not exist.",
  )
  .refine(
    (value) => new Date(`${value}T00:00:00Z`) <= new Date(),
    "That date is in the future.",
  );

export const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .email("That does not look like an email address.")
  .max(254);

/**
 * Creating an account.
 *
 * Note what is *not* here: a group. Registration and joining a group were one
 * step, which meant an account could not exist without a group, a second
 * invitation had nowhere to go, and the two very different questions — may you
 * have an account, and may you see these photographs — were answered by one
 * code. They are separate now: this makes the account, and `groups.join` deals
 * with the rest.
 *
 * `code` is optional because it is temporary. It gates the service while it is
 * private, and `PUBLIC_REGISTRATION` retires the requirement without any of
 * this changing.
 */
/**
 * The keypair a browser generates before it ever calls the server.
 *
 * All of it is safe to hand over: the public half is public by definition, and
 * the private half arrives already sealed under a key derived from the
 * password — which the server never receives, never derives, and cannot obtain
 * from the hash it stores for sign-in.
 *
 * That is the whole property this system rests on. Somebody who takes the
 * database gets these rows and can do nothing with them; somebody who changes
 * an address and resets a password gets the account and not the photographs,
 * because a reset cannot produce a key the server never held.
 */
export const setupKeysInput = z.object({
  /**
   * Proof that this is the account holder and not a stolen session.
   *
   * The password is what seals the private key, so it is in memory at exactly
   * this moment anyway — and requiring it means somebody who has taken a
   * session cannot plant a keypair they control and quietly become the account
   * that every future group grant is sealed to.
   */
  password: z.string().min(1).max(512),
  keys: z.lazy(() => keyBundleSchema),
});

export const keyBundleSchema = z.object({
  /** SPKI, base64. */
  publicKey: z.string().min(1).max(8192),
  /** PKCS#8, sealed under the password-derived key. Base64. */
  wrappedPrivateKey: z.string().min(1).max(8192),
  /** Base64. Per user, so two people with one password derive different keys. */
  keySalt: z.string().min(1).max(256),
  kdfIterations: z.number().int().min(100_000).max(10_000_000),
  /**
   * The same private key, sealed a second time under a printable recovery code
   * shown once and never stored. Without it, a forgotten password can only be
   * undone by another member — impossible for the last member of a group.
   */
  wrappedPrivateKeyByCode: z.string().min(1).max(8192).optional(),
  recoveryCodeSalt: z.string().min(1).max(256).optional(),
});

export const registerInput = z
  .object({
    code: accessCodeSchema.optional(),
    username: usernameSchema,
    password: passwordSchema,
    /**
     * Optional only so that a client which has not been updated still works.
     * An account without one cannot hold encrypted media, and the interface
     * always sends it.
     */
    keys: keyBundleSchema.optional(),
    firstName: nameSchema,
    lastName: nameSchema,
    dateOfBirth: dateOfBirthSchema,
    email: emailSchema,
    /** Defaults to the first name, which is what people are called in a group. */
    displayName: displayNameSchema.optional(),
  })
  .refine(
    (value) => ageOn(value.dateOfBirth, new Date()) >= MINIMUM_AGE_YEARS,
    {
      path: ["dateOfBirth"],
      message: `You need to be at least ${String(MINIMUM_AGE_YEARS)} to sign up.`,
    },
  );

/**
 * Changing a password.
 *
 * The re-sealed private key is optional only because accounts predating
 * encryption have none. Where one exists it is not optional at all: the private
 * key is sealed under a key derived from the password, so a password that
 * changed without it being re-sealed would leave the person locked out of every
 * photo they own — and the server cannot re-seal it, having never seen either
 * the old key or the new one.
 *
 * The work therefore happens in the browser, and what arrives here is the
 * result: a new hash to check future sign-ins against, and an opaque blob to
 * store. Both are written together or not at all.
 */
export const changePasswordInput = z.object({
  currentPassword: z.string().min(1).max(200),
  newPassword: passwordSchema,
  reSealed: z
    .object({
      wrappedPrivateKey: z.string().min(1).max(8192),
      /** Base64. New salt, because the derivation changed. */
      keySalt: z.string().min(1).max(256),
      kdfIterations: z.number().int().min(100_000).max(10_000_000),
    })
    .optional(),
});

/* ───────────────────────────────── Groups ────────────────────────────────── */

export const groupNameSchema = z
  .string()
  .trim()
  .min(1, "Give the group a name.")
  .max(60);

/**
 * A single emoji. Restricting to one grapheme stops a "name" being smuggled
 * into the icon slot and breaking every layout that assumes a square.
 */
export const emojiSchema = z
  .string()
  .trim()
  .min(1)
  .max(8)
  .refine((value) => [...new Intl.Segmenter().segment(value)].length === 1, {
    message: "Choose a single emoji.",
  });

/**
 * Group accent colours are limited to a curated, contrast-checked set.
 * Free-form colour input is how a design system dies: a user picks #FFFF00 and
 * every piece of white text on it becomes unreadable.
 */
export const ACCENT_COLORS = [
  "violet",
  "blue",
  "cyan",
  "teal",
  "lime",
  "amber",
  "rose",
  "slate",
] as const;

export const accentColorSchema = z.enum(ACCENT_COLORS);

export const groupSettingsSchema = z.object({
  membersCanUpload: z.boolean(),
  membersCanCreateTrips: z.boolean(),
  membersCanDeleteOwnFiles: z.boolean(),
  membersCanDeleteOthersFiles: z.boolean(),
  membersCanDownload: z.boolean(),
  membersCanShareExternally: z.boolean(),
  membersCanCreatePosts: z.boolean(),
  membersCanComment: z.boolean(),
  membersCanInvite: z.boolean(),
  allowOriginalDownloads: z.boolean(),
});

export const createGroupInput = z.object({
  name: groupNameSchema,
  description: z.string().trim().max(500).optional(),
  emoji: emojiSchema.optional(),
  accentColor: accentColorSchema.optional(),
  /**
   * The group key, generated in the browser and sealed to the creator.
   *
   * The server never sees the key itself, only this. A group created without
   * one exists but can hold no encrypted media - which is what happens for a
   * client that predates encryption, and why it is optional rather than
   * required.
   */
  wrappedGroupKey: z.string().min(1).max(8192).optional(),
  /**
   * The same key, sealed to the recovery key, so the group is covered from
   * the moment it exists rather than whenever somebody remembers.
   *
   * Both or neither: an id without a key seals nothing, and a key without an
   * id cannot be filed against the key that opens it.
   */
  escrowedGroupKey: z.string().min(1).max(8192).optional(),
  recoveryKeyId: zId("recovery").optional(),
});

export const updateGroupInput = z.object({
  groupId: zId("group"),
  name: groupNameSchema.optional(),
  description: z.string().trim().max(500).nullable().optional(),
  emoji: emojiSchema.nullable().optional(),
  accentColor: accentColorSchema.optional(),
  coverFileId: zId("file").nullable().optional(),
  galleryDensity: z.enum(["comfortable", "compact"]).optional(),
});

/**
 * Deleting a group is deliberately awkward (§100). The typed confirmation must
 * match the group's name exactly, and a fresh TOTP code is mandatory. Both are
 * re-verified server-side against freshly loaded state.
 */
export const deleteGroupInput = z.object({
  groupId: zId("group"),
  confirmName: z.string().min(1),
  totpCode: totpCodeSchema,
});

/* ──────────────────────────────── Invites ────────────────────────────────── */

export const createInviteInput = z.object({
  groupId: zId("group"),
  kind: z.enum(["link", "code"]),
  label: z.string().trim().max(60).optional(),
  roleOnJoin: z.enum(GROUP_ROLES).default("member"),
  maxUses: z.number().int().min(1).max(1000).nullable().default(null),
  expiresInHours: z
    .number()
    .int()
    .min(1)
    .max(24 * 365)
    .nullable()
    .default(null),
  /**
   * The group key, sealed under a key derived from the code being issued.
   *
   * Produced by the inviting member, who is the only party that holds the group
   * key: the server cannot seal it, and a stranger has nobody to receive it
   * from. Sent together with the salt so the recipient can derive the same key
   * from the code in their hand.
   */
  wrappedGroupKey: z.string().min(1).max(8192).optional(),
  keySalt: z.string().min(1).max(256).optional(),
});

/** A member re-sealing the group key to their own public half, after joining. */
/** One member sealing the group key to another member's public half. */
export const grantGroupKeyInput = z.object({
  groupId: zId("group"),
  userId: zId("user"),
  wrappedGroupKey: z.string().min(1).max(8192),
});

export const sealInviteInput = z.object({
  groupId: zId("group"),
  inviteId: z.string().min(1).max(64),
  wrappedGroupKey: z.string().min(1).max(8192),
  /** Base64. The salt the code was stretched with. */
  keySalt: z.string().min(1).max(256),
});

export const storeGroupKeyInput = z.object({
  groupId: zId("group"),
  wrappedGroupKey: z.string().min(1).max(8192),
});

/* ──────────────────────────────── Folders ────────────────────────────────── */

export const createFolderInput = z.object({
  groupId: zId("group"),
  name: z.string().trim().min(1, "Give it a name.").max(80),
  kind: z.enum(["folder", "trip"]).default("trip"),
  parentId: zId("folder").nullable().default(null),
  description: z.string().trim().max(1000).optional(),
  startDate: z.string().date().nullable().optional(),
  endDate: z.string().date().nullable().optional(),
  locationName: z.string().trim().max(120).optional(),
  /**
   * ISO 3166-1 alpha-2, upper case. Empty for an album that is not about a
   * country — “the cabin”, “Sarah’s wedding”.
   *
   * A list, because a fortnight's driving is one album and three countries.
   * Capped at 20: a single trip through more than that is not a thing this
   * field is for, and an unbounded array is an unbounded row.
   *
   * Validated as a shape only. Checking each code against a list here would
   * mean carrying a copy of that list on the server, which would then disagree
   * with whatever `Intl.DisplayNames` knows in the browser the moment either
   * side updates.
   */
  countryCodes: z
    .array(
      z
        .string()
        .trim()
        .regex(/^[A-Za-z]{2}$/)
        .transform((value) => value.toUpperCase()),
    )
    .max(20)
    .optional(),
  /**
   * The year the trip happened.
   *
   * Its own field rather than a reading of `startDate`, because almost nobody
   * fills in exact dates and almost everybody knows the year — and the album
   * list is sorted by it, so it needs to be answerable without them.
   */
  year: z.number().int().min(1826).max(2200).nullable().optional(),
  emoji: emojiSchema.optional(),
  /** Null inherits the group's accent. Free-form colour is not accepted. */
  accentColor: accentColorSchema.nullable().optional(),
});

/* ──────────────────────────────── Uploads ────────────────────────────────── */

/** Lowercase hex SHA-256, computed by the client before the transfer starts. */
export const sha256Schema = z
  .string()
  .regex(/^[a-f0-9]{64}$/, "Invalid checksum.");

export const createUploadInput = z
  .object({
    groupId: zId("group"),
    folderId: zId("folder").nullable().default(null),
    name: z.string().trim().min(1).max(255),
    mime: z.string().trim().min(1).max(120),
    sizeBytes: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
    /**
     * SHA-256 of the bytes storage will hold.
     *
     * For a plaintext upload that is the file. For a sealed one it is the
     * *ciphertext* — deliberately, because the hash of the plaintext is a
     * fingerprint that would let anyone holding the database check whether a
     * photograph they already have is in here, which is the exact question
     * encryption is meant to leave unanswerable.
     */
    sha256: sha256Schema,

    /**
     * Group-scoped deduplication tag: HMAC(group key, SHA-256 of the plaintext).
     *
     * Sent instead of the plaintext hash for sealed uploads, and it is what makes
     * "we already have this photograph" answerable without making "do you have
     * this photograph" answerable. See `contentTag` for the full argument.
     *
     * Absent for plaintext uploads, which dedupe on `sha256` as they always did.
     */
    contentTag: sha256Schema.optional(),

    /** Client's local capture time, used only when EXIF has none. */
    takenAt: z.string().datetime().nullable().optional(),
    /** Idempotency key so a retried "create" resumes rather than duplicating. */
    clientUploadId: z.string().trim().min(8).max(64),

    /**
     * How this object is protected, decided per file and not per installation.
     *
     *  · `e2e`    — the browser resized, sealed and is about to upload ciphertext.
     *                No server holds a key. Photographs in formats a browser can
     *                decode take this path.
     *  · `server` — sealed too, but the key is also wrapped to a server key so a
     *                worker can transcode it. Video, and anything a browser
     *                cannot open.
     *  · `none`   — plaintext, as everything was before this existed.
     */
    encryption: z.enum(["none", "e2e", "server"]).default("none"),

    /**
     * The file key, sealed with the group key.
     *
     * Required whenever `encryption` is not `none` — a sealed object whose key
     * was never recorded is unreadable by everyone including its owner, which is
     * indistinguishable from data loss and is worth refusing at the boundary.
     */
    wrappedFileKey: z.string().min(1).max(8192).optional(),

    /**
     * Byte length of the sealed object, which is what storage will hold.
     *
     * Sent separately from `sizeBytes` because the two differ: sealing adds a
     * header and a tag per chunk. The reservation has to be taken against what is
     * actually stored, or a group could exceed its quota by the difference.
     */
    encryptedSizeBytes: z
      .number()
      .int()
      .positive()
      .max(Number.MAX_SAFE_INTEGER)
      .optional(),

    /**
     * Derivatives the browser produced, since no worker can produce them for an
     * end-to-end file. Declared here so `create` can hand back a signed URL for
     * each one in the same round trip.
     */
    variants: z
      .array(
        z.object({
          variant: z.enum(["thumb", "preview"]),
          format: z.enum(["webp"]),
          width: z.number().int().positive().max(20000),
          height: z.number().int().positive().max(20000),
          encryptedSizeBytes: z
            .number()
            .int()
            .positive()
            .max(Number.MAX_SAFE_INTEGER),
        }),
      )
      .max(4)
      .optional(),
  })
  .refine(
    (value) => value.encryption === "none" || Boolean(value.wrappedFileKey),
    {
      path: ["wrappedFileKey"],
      message: "An encrypted upload must carry its wrapped file key.",
    },
  )
  .refine(
    (value) => value.encryption === "none" || Boolean(value.encryptedSizeBytes),
    {
      path: ["encryptedSizeBytes"],
      message: "An encrypted upload must declare its sealed size.",
    },
  );

/**
 * `groupId` is present on every upload step, not just the first. It is what the
 * permission middleware verifies membership against, so a step that omitted it
 * would have to infer the group from the file — exactly the pattern that makes
 * IDOR possible.
 */
export const completeUploadInput = z.object({
  groupId: zId("group"),
  uploadId: z.string().trim().min(1).max(400),
  fileId: zId("file"),
  parts: z
    .array(
      z.object({
        partNumber: z.number().int().min(1).max(10_000),
        etag: z.string().trim().min(1).max(200),
      }),
    )
    .min(1)
    .max(10_000),
});

export const abortUploadInput = z.object({
  groupId: zId("group"),
  fileId: zId("file"),
  uploadId: z.string().trim().min(1).max(400),
});

/* ──────────────────────────────── Gallery ────────────────────────────────── */

export const galleryFilterSchema = z.object({
  folderId: zId("folder").nullable().optional(),
  /** Include files in nested folders, for a trip that has sub-albums. */
  includeDescendants: z.boolean().default(false),
  kind: z.enum(FILE_KINDS).optional(),
  uploaderId: zId("user").optional(),
  favoritesOnly: z.boolean().default(false),
  /** Inclusive ISO dates. */
  from: z.string().date().optional(),
  to: z.string().date().optional(),
  year: z.number().int().min(1900).max(2200).optional(),
  month: z.number().int().min(1).max(12).optional(),
  camera: z.string().trim().max(80).optional(),
  hasLocation: z.boolean().optional(),
  search: z.string().trim().max(120).optional(),
});

export const listFilesInput = z.object({
  groupId: zId("group"),
  filter: galleryFilterSchema.partial().optional(),
  sort: z.enum(GALLERY_SORTS).default("taken_desc"),
  cursor: z.string().max(512).nullable().default(null),
  limit: z.number().int().min(1).max(MAX_PAGE_SIZE).default(100),
});

/**
 * Put derivatives back on a file that has lost them.
 *
 * The server cannot do this itself for a sealed file — it holds no key, so it
 * cannot decode the original to resize it. Only a member's browser can, and
 * only while its keyring is open. This is the endpoint that lets it hand the
 * result back.
 *
 * Deliberately not part of `create`: the file already exists, its bytes are
 * already stored and already charged, and nothing about the original changes.
 * What is being replaced is the small copies beside it.
 */
export const rebuildVariantsInput = z.object({
  groupId: zId("group"),
  fileId: zId("file"),
  variants: z
    .array(
      z.object({
        variant: z.enum(["thumb", "preview"]),
        format: z.enum(["webp"]),
        width: z.number().int().positive().max(20000),
        height: z.number().int().positive().max(20000),
        encryptedSizeBytes: z
          .number()
          .int()
          .positive()
          .max(Number.MAX_SAFE_INTEGER),
      }),
    )
    .min(1)
    .max(4),
});

export const mediaUrlInput = z.object({
  fileId: zId("file"),
  variant: z.enum(VARIANTS).default("preview"),
  /** Sent so the server can pick AVIF vs WebP without a round trip. */
  accept: z.string().max(200).optional(),
  /**
   * What the caller intends to do with an original: look at it, or save it.
   *
   * Only meaningful for `variant: "original"`, and defaulted to `download`
   * because that is both the older behaviour and the safer one. `view` asks for
   * a response the browser will render, which is what a full-resolution viewer
   * needs and what makes iOS's "Save to Photos" work — see `SignedGetOptions`
   * in `@fc/storage` for why those are the same problem.
   *
   * A request, not an instruction: the server grants it only for image types it
   * has validated, and answers with a download for anything else.
   */
  intent: z.enum(["download", "view"]).default("download"),
});

/**
 * Signing a batch of media URLs.
 *
 * The cap is a *transport* limit, not a database one. This is a query, so the
 * ids travel in the query string, and a hundred of them is a 3,800-character
 * URL against the 2,048 the client allows — the request then fails outright and
 * every file in it silently loses its URL. Five hundred was permitted here and
 * could never have arrived.
 *
 * Refused loudly at forty rather than accepted and lost in transit: a caller
 * that batches too greedily gets a validation error naming the problem instead
 * of a whole screen of grey squares. `fetchSignedUrls` on the client chunks at
 * twenty and is what everything should go through.
 *
 * Forty, not fifty. Fifty was arithmetic done against a 26-character file id;
 * the server actually mints 30-character ones, which puts a full fifty at
 * 2,134 characters — over the limit this cap exists to keep callers inside.
 * A cap that is itself unsendable is worse than no cap, because it reads as
 * permission.
 */
export const batchMediaUrlInput = z.object({
  fileIds: z.array(zId("file")).min(1).max(40),
  variant: z.enum(VARIANTS).default("thumb"),
  accept: z.string().max(200).optional(),
});

/* ───────────────────────────────── Social ────────────────────────────────── */

/** Long enough for a trip write-up, short enough that the feed stays a feed. */
export const postBodySchema = z
  .string()
  .trim()
  .max(5000, "That's too long for one post.");

export const commentBodySchema = z
  .string()
  .trim()
  .min(1, "Say something first.")
  .max(2000, "That's too long for a comment.");

/**
 * Reactions come from a curated set, for the same reason accent colours do.
 * "An emoji" from a client is just text: a ZWJ sequence renders as something
 * unrecognisable, and a plain string survives every length check while being
 * neither an emoji nor safe to echo back into every feed that shows the post.
 * A closed set also means the client can render tallies without a font gamble.
 */
export const REACTION_EMOJI = [
  "👍",
  "👎",
  "❤️",
  "😂",
  "😮",
  "😢",
  "🎉",
  "🔥",
] as const;

export const reactionEmojiSchema = z.enum(REACTION_EMOJI);

export type ReactionEmoji = (typeof REACTION_EMOJI)[number];

export const REACTION_TARGETS = ["post", "comment", "file"] as const;

export type ReactionTarget = (typeof REACTION_TARGETS)[number];

export const createPostInput = z
  .object({
    groupId: zId("group"),
    body: postBodySchema.default(""),
    /** Files a post is attached to keep living in their album, if it has one. */
    folderId: zId("folder").nullable().default(null),
    fileIds: z.array(zId("file")).max(50).default([]),
  })
  .refine((value) => value.body.length > 0 || value.fileIds.length > 0, {
    message: "Write something or add a photo.",
    path: ["body"],
  });

export const listPostsInput = z.object({
  groupId: zId("group"),
  cursor: z.string().max(512).nullable().default(null),
  limit: z.number().int().min(1).max(MAX_PAGE_SIZE).default(20),
});

export const deletePostInput = z.object({
  groupId: zId("group"),
  postId: zId("post"),
});

/**
 * A comment hangs off a post or off a photo, never both — the schema models
 * them as two nullable columns, so "exactly one" has to be asserted here.
 */
const oneTarget = {
  check: (value: { postId: string | null; fileId: string | null }) =>
    Boolean(value.postId) !== Boolean(value.fileId),
  message: "Comment on a post or a photo, not both.",
};

export const createCommentInput = z
  .object({
    groupId: zId("group"),
    postId: zId("post").nullable().default(null),
    fileId: zId("file").nullable().default(null),
    body: commentBodySchema,
  })
  .refine(oneTarget.check, { message: oneTarget.message, path: ["postId"] });

export const listCommentsInput = z
  .object({
    groupId: zId("group"),
    postId: zId("post").nullable().default(null),
    fileId: zId("file").nullable().default(null),
    limit: z.number().int().min(1).max(MAX_PAGE_SIZE).default(100),
  })
  .refine(oneTarget.check, { message: oneTarget.message, path: ["postId"] });

export const deleteCommentInput = z.object({
  groupId: zId("group"),
  commentId: zId("comment"),
});

export const setReactionInput = z
  .object({
    groupId: zId("group"),
    targetType: z.enum(REACTION_TARGETS),
    targetId: z.string().max(64),
    emoji: reactionEmojiSchema,
    /**
     * The desired state, not "flip it". Two taps racing each other settle on
     * whichever request lands last either way, but only an explicit state lets
     * the client and the server agree on what that outcome was.
     */
    active: z.boolean().default(true),
  })
  .refine((value) => isId(value.targetType, value.targetId), {
    message: "That id doesn't match the thing being reacted to.",
    path: ["targetId"],
  });

/* ───────────────────────────── Share links ───────────────────────────────── */

/**
 * A key wrapped to an RSA-3072 public key: exactly 384 bytes, base64.
 *
 * The server cannot tell a *correct* wrapped key from an incorrect one — it
 * holds no private key and that is the entire design. It can tell one from a
 * sentence, though, and the difference matters: a hand-edited regrant file was
 * accepted with `"wrappedGroupKey": "obviously-not-a-wrapped-key"` and stored
 * verbatim, which looked from the outside like the recovery keys doing nothing.
 *
 * They were doing their job — a grant that does not unwrap opens no
 * photographs, and that is checked in `tamperedRegrant.test.ts`. But accepting
 * a value that cannot possibly be a key, and reporting success, is a bad way
 * to demonstrate it. This refuses the shapes nobody could have produced
 * honestly, and stays silent about the rest, because silence is all it is
 * entitled to.
 */
export const wrappedKeySchema = z
  .string()
  .trim()
  .refine(
    (value) => {
      /* Base64 of 384 bytes is 512 characters, with no padding to spare. */
      if (
        !/^[A-Za-z0-9+/]{511}=$/.test(value) &&
        !/^[A-Za-z0-9+/]{512}$/.test(value)
      ) {
        return false;
      }
      try {
        return base64ByteLength(value) === 384;
      } catch {
        return false;
      }
    },
    {
      message:
        "That is not a key this system could have produced. Re-run the " +
        "recovery command rather than editing the file it wrote.",
    },
  );

/** Decoded length of a base64 string, without decoding it. */
function base64ByteLength(value: string): number {
  const padding = value.endsWith("==") ? 2 : value.endsWith("=") ? 1 : 0;
  return (value.length / 4) * 3 - padding;
}
export const createShareLinkInput = z
  .object({
    groupId: zId("group"),
    fileIds: z.array(zId("file")).max(1000).optional(),
    folderId: zId("folder").optional(),
    expiresInHours: z
      .number()
      .int()
      .min(1)
      .max(24 * 365)
      .nullable()
      .default(null),
    password: z.string().min(4).max(128).nullable().default(null),
    allowDownload: z.boolean().default(true),
    allowOriginals: z.boolean().default(false),
    stripExif: z.boolean().default(true),
    /**
     * Each file’s key, re-wrapped under a key that lives only in the link’s
     * URL fragment.
     *
     * Supplied by the browser, because only a browser can open the group-
     * sealed originals. A mutation, so this travels in the body: a thousand
     * of these would be nowhere near a URL length limit but is comfortably
     * inside a request body.
     */
    itemKeys: z
      .array(
        z.object({
          fileId: zId("file"),
          sealedFileKey: z.string().min(1).max(512),
        }),
      )
      .max(1000)
      .optional(),
  })
  .refine(
    (value) => Boolean(value.folderId) !== Boolean(value.fileIds?.length),
    {
      message: "Share either a folder or a set of files, not both.",
    },
  );

/* ──────────────────────────── Members & roles ────────────────────────────── */

export const updateMemberInput = z.object({
  groupId: zId("group"),
  userId: zId("user"),
  role: z.enum(GROUP_ROLES).optional(),
  overrides: z
    .object({
      grant: z.array(z.enum(CAPABILITIES)).max(CAPABILITIES.length).optional(),
      deny: z.array(z.enum(CAPABILITIES)).max(CAPABILITIES.length).optional(),
    })
    .nullable()
    .optional(),
});

/* ───────────────────────────── Platform admin ────────────────────────────── */

export const setQuotaInput = z.object({
  groupId: zId("group"),
  limitBytes: z.number().int().positive(),
  totpCode: totpCodeSchema,
  reason: z.string().trim().max(200).optional(),
});

export type LoginInput = z.infer<typeof loginInput>;
export type RegisterInput = z.infer<typeof registerInput>;
export type CreateUploadInput = z.infer<typeof createUploadInput>;
export type ListFilesInput = z.infer<typeof listFilesInput>;
export type GalleryFilter = z.infer<typeof galleryFilterSchema>;
export type CreateShareLinkInput = z.infer<typeof createShareLinkInput>;
export type AccentColor = (typeof ACCENT_COLORS)[number];
export type CreatePostInput = z.infer<typeof createPostInput>;
export type CreateCommentInput = z.infer<typeof createCommentInput>;
export type SetReactionInput = z.infer<typeof setReactionInput>;

/* ─────────────────────────────── Faces ───────────────────────────────────── */

/**
 * A face, as the server ever sees one.
 *
 * The vector and the box arrive already sealed, and there is deliberately no
 * schema for their contents — the server cannot validate what it cannot read,
 * and pretending otherwise would invite a shape check that only works while
 * the data is in the clear.
 */
export const sealedFaceSchema = z.object({
  faceIndex: z.number().int().min(0).max(255),
  /** Sealed. A 128-float embedding is a little over 700 bytes once armoured. */
  sealedVector: z.string().min(1).max(8192),
  sealedBox: z.string().min(1).max(2048),
  quality: z.number().min(0).max(1),
});

/** Model ids are ours, not user input, but they reach the server as strings. */
const modelId = z.string().trim().min(1).max(120);

export const storeFacesInput = z.object({
  groupId: zId("group"),
  fileId: zId("file"),
  modelId,
  /** A photograph with sixty faces in it is a stadium, not a memory. */
  faces: z.array(sealedFaceSchema).max(64),
});

/**
 * Which of these photographs has the model not looked at yet?
 *
 * Capped at fifty for the same reason as `batchMediaUrlInput`: a query carries
 * its input in the URL, and five hundred ids is seventeen thousand characters —
 * a request that cannot be sent, on a path where failing means face scanning
 * quietly does nothing in exactly the large libraries that need it.
 *
 * Forty for the same reason as `batchMediaUrlInput`, and this one had the
 * failure to show for it: the client asked for fifty, the model id is long,
 * and the batching layer threw “Input is too big for a single dispatch”
 * before the request was sent. People opened to an empty strip in every
 * group with more than fifty photographs.
 */
export const pendingFacesInput = z.object({
  groupId: zId("group"),
  modelId,
  fileIds: z.array(zId("file")).max(40),
});

export const listFacesInput = z.object({
  groupId: zId("group"),
  modelId,
  limit: z.number().int().min(1).max(1000).default(500),
  /** The last file id of the previous page. Faces never straddle a page. */
  afterFileId: zId("file").optional(),
});

export const faceCountInput = z.object({
  groupId: zId("group"),
  modelId,
});

export const namePersonInput = z.object({
  groupId: zId("group"),
  modelId,
  /** `fileId:faceIndex` of the cluster's most central face. */
  clusterKey: z.string().trim().min(3).max(120),
  /** Sealed, or null to forget the name entirely. */
  sealedName: z.string().min(1).max(2048).nullable(),
});

export type StoreFacesInput = z.infer<typeof storeFacesInput>;
export type ListFacesInput = z.infer<typeof listFacesInput>;
export type NamePersonInput = z.infer<typeof namePersonInput>;

/**
 * Hide a person, or bring them back.
 *
 * Not a delete. The faces stay in the photographs and the next scan finds them
 * again, so what is stored is the decision to stop showing somebody — a
 * stranger in the background of three shots — rather than any attempt to
 * remove them from the pictures they are in.
 */
export const hidePersonInput = z.object({
  groupId: zId("group"),
  modelId,
  clusterKey: z.string().trim().min(3).max(120),
  hidden: z.boolean(),
});

export type HidePersonInput = z.infer<typeof hidePersonInput>;

/* ────────────────────────────── Your account ─────────────────────────────── */

/**
 * A profile picture, as a data URL, or null to remove it.
 *
 * Capped hard. The browser resizes to 256px before sending, so anything much
 * larger than this is either a bug or somebody calling the API directly, and
 * both should be refused rather than stored: this column is read back for
 * every member of every group.
 */
export const setAvatarInput = z.object({
  image: z
    .string()
    .regex(
      /^data:image\/(jpeg|png|webp);base64,[A-Za-z0-9+/=]+$/,
      "That is not an image.",
    )
    .max(120_000)
    .nullable(),
});

export const updateProfileInput = z.object({
  /** What other members see. Empty is not a name; the field is required. */
  displayName: z.string().trim().min(1, "This is required.").max(60),
});

/**
 * Changing the address on an account.
 *
 * The current password is required, and that is not ceremony. An email address
 * is the route back into an account, so anyone who reaches an unattended signed-in
 * browser could otherwise point the account at an address of their own and take
 * it — the session alone must not be enough.
 */
export const changeEmailInput = z.object({
  email: z
    .string()
    .trim()
    .email("That does not look like an email address.")
    .max(254),
  currentPassword: z.string().min(1).max(200),
});

export type UpdateProfileInput = z.infer<typeof updateProfileInput>;
export type ChangeEmailInput = z.infer<typeof changeEmailInput>;
